/**
 * Quick Pin Templates — 2-second pin entry from the field.
 *
 * Each template carries a category + priority + a placeholder title that
 * a superintendent can either accept as-is or tweak in one tap. The goal
 * is to convert what used to be a 20-second typing exercise into a 2-second
 * tap. Categories match PIN_CATEGORY in lib/utils.
 *
 * Per Phase 6 reset directive: do not add ERP-style workflow here. These
 * are intentionally lightweight title presets, not workflow templates.
 */

export const QUICK_PIN_TEMPLATES = [
  {
    id: "trench_depth",
    label: "Trench depth verify",
    title: "Trench needs depth verification",
    category: "inspection_item",
    priority: "high",
  },
  {
    id: "soft_subgrade",
    label: "Soft subgrade",
    title: "Soft subgrade — needs proof roll",
    category: "field_issue",
    priority: "high",
  },
  {
    id: "mot_conflict",
    label: "MOT conflict",
    title: "MOT conflict on this sheet",
    category: "field_issue",
    priority: "high",
  },
  {
    id: "utility_conflict",
    label: "Utility conflict",
    title: "Utility conflict — needs RFI",
    category: "utility_conflict",
    priority: "high",
  },
  {
    id: "rebar_spacing",
    label: "Rebar spacing",
    title: "Reinforcement spacing off",
    category: "inspection_item",
    priority: "medium",
  },
  {
    id: "form_alignment",
    label: "Form alignment",
    title: "Form alignment issue",
    category: "field_issue",
    priority: "medium",
  },
  {
    id: "striping_conflict",
    label: "Striping conflict",
    title: "Striping conflict with plan",
    category: "rfi_needed",
    priority: "medium",
  },
  {
    id: "shoulder_drop",
    label: "Shoulder drop-off",
    title: "Shoulder drop-off — safety",
    category: "safety_concern",
    priority: "critical",
  },
  {
    id: "survey_needed",
    label: "Survey verify",
    title: "Survey verification needed",
    category: "survey_layout",
    priority: "medium",
  },
  {
    id: "rcp_joint",
    label: "RCP joint",
    title: "RCP joint issue",
    category: "field_issue",
    priority: "medium",
  },
];


/* ------------------------------------------------------------------ *
 * Iter 67 Phase 5 — Recent template persistence (localStorage).      *
 *                                                                    *
 * Foreman who dropped {trench_depth, soft_subgrade, rcp_joint} three *
 * times during the morning shouldn't have to re-scroll through 15    *
 * templates each time. We remember the last 5 templates they used    *
 * (most recent first) and the Quick Pin chip strip surfaces them as  *
 * a "Recent" row at the top.                                         *
 *                                                                    *
 * Storage:                                                            *
 *   localStorage key: fop_qpt_recent_v1                              *
 *   value: JSON array of template id strings, length ≤ 5             *
 * ------------------------------------------------------------------ */
const RECENT_KEY = "fop_qpt_recent_v1";
const RECENT_CAP = 5;

export function getRecentTemplateIds() {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENT_CAP) : [];
  } catch {
    return [];
  }
}

export function recordTemplateUse(templateId) {
  if (!templateId) return;
  try {
    const cur = getRecentTemplateIds().filter((id) => id !== templateId);
    cur.unshift(templateId);
    window.localStorage.setItem(
      RECENT_KEY,
      JSON.stringify(cur.slice(0, RECENT_CAP)),
    );
  } catch { /* private mode — silently ignore */ }
}

/** Return the templates split into [recent, rest], preserving the
 *  original order of `rest`. `recent` is in most-recently-used order. */
export function partitionTemplatesByRecent() {
  const ids = new Set(getRecentTemplateIds());
  const idIndex = new Map(getRecentTemplateIds().map((id, i) => [id, i]));
  const recent = QUICK_PIN_TEMPLATES
    .filter((t) => ids.has(t.id))
    .sort((a, b) => idIndex.get(a.id) - idIndex.get(b.id));
  const rest = QUICK_PIN_TEMPLATES.filter((t) => !ids.has(t.id));
  return { recent, rest };
}
